import { useEffect, useMemo, useRef } from "react";
import { useStore } from "zustand";
import { AppWindow } from "../apps/AppWindow";
import { APP_REGISTRY } from "../apps/registry";
import { playSfx } from "../audio/sound";
import { artifactNav, pinArtifact } from "../case/artifactNav";
import { useKase } from "../case/useCase";
import { moduleAppDefs } from "../modules/registry";
import { useGameStore } from "../state/gameStore";
import { AppIcon } from "../ui/icons";
import { useWindowStore, windowStore } from "../wm/useWindowStore";
import { selectFocusedId } from "../wm/windowStore";
import { notificationStore } from "./notificationStore";
import { QuoteLayer } from "./QuoteLayer";
import styles from "./shell.module.css";
import { Taskbar } from "./Taskbar";

/**
 * Escritorio ArgOS (iteración UI 2026-08-13): convenciones de OS real de la
 * época diegética (2011) con skin deco-noir — un clic SELECCIONA el ícono,
 * doble clic ABRE; wallpaper generado (ojo de Argos sobre Costa Vespera);
 * clic en el vacío deselecciona. La identidad no se negocia: nada de clonar
 * Windows, sí sus affordances.
 */

const vp = () => ({ vw: window.innerWidth, vh: window.innerHeight });

/** ~12 min de estancamiento real disparan el hint nivel 0 (jugabilidad §8) */
const HINT0_IDLE_MS = 12 * 60 * 1000;

interface Props {
  detectiveName: string;
}

export function Desktop({ detectiveName }: Props) {
  const windows = useWindowStore((s) => s.windows);
  const notifications = useStore(notificationStore, (s) => s.items);

  // Bienvenida diegética al montar el escritorio (una sola vez por sesión)
  useEffect(() => {
    if (notificationStore.getState().items.length === 0) {
      notificationStore
        .getState()
        .push("M.", `Expediente montado. La Dirección observa, ${detectiveName}.`, Date.now());
    }
  }, [detectiveName]);

  // F1-T03: notificaciones del CASO (reglas/eventos del motor) → toasts del OS.
  // Las presentes al montar son historia (replay de un save): no se re-toastean.
  const caseNotifications = useGameStore((s) => s.derived?.notifications);
  const seenCaseNotifs = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!caseNotifications) return;
    if (seenCaseNotifs.current === null) {
      seenCaseNotifs.current = new Set(caseNotifications.map((n) => n.id));
      return;
    }
    for (const n of caseNotifications) {
      if (!seenCaseNotifs.current.has(n.id)) {
        seenCaseNotifs.current.add(n.id);
        notificationStore.getState().push(n.title, n.body, Date.now());
      }
    }
  }, [caseNotifications]);

  // F1-T05 nivel 0: ~12 min sin fijar evidencia ni resolver un hito ⇒ la
  // notificación ambiental del caso (hint level 0), una sola vez por sesión.
  const kase = useKase();
  const hint0Fired = useRef(false);
  const lastProgressRef = useRef(Date.now());
  const pinsCount = useGameStore((s) => s.derived?.pinnedEvidence.length ?? 0);
  const milestonesSolved = useGameStore(
    (s) => Object.values(s.derived?.milestones ?? {}).filter((m) => m.solved).length,
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: pins/hitos son las señales de progreso que resetean el timer
  useEffect(() => {
    lastProgressRef.current = Date.now();
  }, [pinsCount, milestonesSolved]);
  useEffect(() => {
    const hint0 = kase.hints.find((h) => h.level === 0);
    if (!hint0) return;
    const check = window.setInterval(() => {
      if (hint0Fired.current) return;
      if (Date.now() - lastProgressRef.current >= HINT0_IDLE_MS) {
        hint0Fired.current = true;
        notificationStore.getState().push("Correo de la Agencia", hint0.text, Date.now());
      }
    }, 30_000);
    return () => window.clearInterval(check);
  }, [kase]);

  // Toasts se asientan a los 4 s (os §1); campanilla al aparecer uno nuevo
  const lastNotifId = useRef(0);
  useEffect(() => {
    const newest = notifications[0];
    if (newest && newest.id > lastNotifId.current) {
      lastNotifId.current = newest.id;
      playSfx("notify");
    }
    const timers = notifications
      .filter((n) => n.toast)
      .map((n) => window.setTimeout(() => notificationStore.getState().settle(n.id), 4000));
    return () => {
      for (const t of timers) window.clearTimeout(t);
    };
  }, [notifications]);

  // Atajos globales (os §1): Tab cicla ventanas; Ctrl+P fija la selección
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        e.preventDefault();
        windowStore.getState().cycleFocus();
        return;
      }
      if (e.ctrlKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        const focusedApp = selectFocusedId(windowStore.getState());
        if (!focusedApp) return;
        const selectedId = artifactNav.getState().selected[focusedApp];
        if (selectedId) pinArtifact(selectedId);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Apps disponibles = core + módulos que el caso instala (F1-T07)
  const allApps = useMemo(() => [...APP_REGISTRY, ...moduleAppDefs(kase)], [kase]);

  const openApp = (id: string) => {
    const app = allApps.find((a) => a.id === id);
    windowStore.getState().open(id, vp(), app?.defaultSize);
  };

  return (
    <div className={styles.desktop} data-testid="desktop">
      <div className={styles.iconGrid}>
        {allApps.map((app) => (
          <button
            key={app.id}
            type="button"
            className={styles.desktopIcon}
            data-testid={`icon-${app.id}`}
            onClick={() => openApp(app.id)}
          >
            <span className={styles.iconGlyph}>
              <AppIcon appId={app.id} size={30} />
            </span>
            <span className={styles.iconLabel}>{app.title}</span>
          </button>
        ))}
      </div>

      {allApps.map((app) => windows[app.id] && <AppWindow key={app.id} app={app} />)}

      {/* Toasts */}
      <div className={styles.toastLane} aria-live="polite">
        {notifications
          .filter((n) => n.toast)
          .map((n) => (
            <output key={n.id} className={styles.toast} data-testid={`toast-${n.id}`}>
              <strong>{n.source}</strong> {n.text}
            </output>
          ))}
      </div>

      <Taskbar />
      <QuoteLayer />
    </div>
  );
}
